// El endpoint PATCH /v2/:id/items-renegociacion actualiza cada ítem existente
// en su lugar, no lo reemplaza: solo escribe las columnas cuya clave el
// payload trae de verdad. Mandar el ítem completo con los campos que el
// vendedor no tocó borra en la fila lo que no viene -- incluida la narrativa
// de la fase, que es el texto que alimenta el documento de la ronda
// siguiente. Este módulo arma ese cuerpo respetando esa semántica.
//
// Una sola excepción, verificada ejecutando la validación real del endpoint:
// tipoItem, refCliente, nombreServicio, tipoMoneda, valorOriginal y
// valorConDescuento son obligatorios en TODO ítem del arreglo, tenga `id` o
// no -- un payload que edita solo el precio y omite los otros cinco se
// rechaza antes de aplicar ningún cambio. Por eso esos seis viajan siempre
// -- con el valor editado si el vendedor lo cambió, o con el valor vigente si
// no -- y escribir el valor vigente sin cambios es inofensivo: el servidor
// vuelve a guardar la misma columna con el mismo dato, no la vacía.

// Campos que el endpoint exige en todo ítem de la renegociación (verificado
// contra su validación real, no solo contra su descripción).
export const CAMPOS_REQUERIDOS_ITEM_RENEGOCIACION = [
  "tipoItem",
  "refCliente",
  "nombreServicio",
  "tipoMoneda",
  "valorOriginal",
  "valorConDescuento",
];

/**
 * Arma el ítem editado que va dentro de `items[]` del body.
 *
 * Los seis campos obligatorios del schema viajan siempre. El resto de las
 * claves -- narrativa, observaciones, lo específico de PLAN o TAREA -- solo
 * entra si `cambios` la trae de verdad: la presencia de la clave en `cambios`
 * es la señal de "esto se edita", no su valor. Un campo que el vendedor dejó
 * en blanco o puso en `null` a propósito viaja igual, porque el filtro es
 * `hasOwnProperty`, no una verificación de verdad/falsedad -- si se filtrara
 * por valor, vaciar un campo dejaría de ser posible desde este formulario.
 *
 * @param {object} original ítem vigente tal como lo entrega el detalle de la propuesta (camelCase, con `id`)
 * @param {object} cambios solo las claves que el vendedor tocó en esta sesión para este ítem
 * @returns {object} ítem listo para `items[]`
 */
export function construirItemEditado(original, cambios) {
  const item = { id: original.id };

  for (const campo of CAMPOS_REQUERIDOS_ITEM_RENEGOCIACION) {
    item[campo] = Object.prototype.hasOwnProperty.call(cambios, campo)
      ? cambios[campo]
      : original[campo];
  }

  for (const [campo, valor] of Object.entries(cambios)) {
    if (CAMPOS_REQUERIDOS_ITEM_RENEGOCIACION.includes(campo)) continue;
    item[campo] = valor;
  }

  return item;
}

/**
 * Cuerpo completo de `PATCH /v2/:id/items-renegociacion`.
 *
 * Reglas que aplica, todas verificadas contra el backend real:
 *
 *  - Un ítem sin cambios registrados no viaja: no hay nada que escribir, y
 *    mandarlo igual (aunque fuera inofensivo) solo agrega ruido a la
 *    bitácora si algún campo quedara mal filtrado más adelante.
 *  - Un ítem marcado para retirar no se edita en la misma llamada: retirar es
 *    la acción final para ese servicio, así que sus cambios pendientes (si
 *    los tenía) se descartan en vez de aplicarse justo antes de sacarlo de la
 *    mesa.
 *  - Un ítem ya aceptado por el cliente no se edita ni se retira aunque el
 *    estado en memoria lo tuviera marcado por error: el backend lo rechaza
 *    con 409, y este filtro evita el viaje al servidor para descubrirlo.
 *  - Los ítems nuevos (sin `id`) viajan completos tal como los arma el
 *    formulario de alta: no tienen fila previa que un patch parcial pudiera
 *    dañar.
 *
 * @param {object} params
 * @param {Array<{id: number}>} [params.itemsVigentes] `propuesta.items` (los que trae el detalle)
 * @param {Record<string|number, object>} [params.cambiosPorItem] cambios sueltos por id de ítem
 * @param {number[]} [params.idsARetirar]
 * @param {Array<object>} [params.itemsNuevos] ítems completos, sin `id`
 * @param {Array<{descripcion: string}>} [params.serviciosSolicitados]
 * @param {number[]} [params.idsAceptados] ids que el cliente ya aceptó -- se excluyen aunque vengan marcados
 * @returns {{items: object[], idsARetirar: number[], serviciosSolicitados: {descripcion: string}[]}}
 */
export function buildAjusteServiciosBody({
  itemsVigentes = [],
  cambiosPorItem = {},
  idsARetirar = [],
  itemsNuevos = [],
  serviciosSolicitados = [],
  idsAceptados = [],
} = {}) {
  const itemsPorId = new Map(itemsVigentes.map((item) => [item.id, item]));
  const idsAceptadosSet = new Set(idsAceptados);

  const idsARetirarFiltrados = idsARetirar.filter(
    (id) => !idsAceptadosSet.has(id),
  );
  const idsARetirarSet = new Set(idsARetirarFiltrados);

  const itemsEditados = Object.entries(cambiosPorItem)
    .filter(([idStr, cambios]) => {
      const id = Number(idStr);
      if (idsAceptadosSet.has(id)) return false;
      if (idsARetirarSet.has(id)) return false;
      if (!cambios || Object.keys(cambios).length === 0) return false;
      return itemsPorId.has(id);
    })
    .map(([idStr, cambios]) =>
      construirItemEditado(itemsPorId.get(Number(idStr)), cambios),
    );

  return {
    items: [...itemsEditados, ...itemsNuevos],
    idsARetirar: idsARetirarFiltrados,
    serviciosSolicitados: serviciosSolicitados
      .map((solicitud) => ({
        descripcion: (solicitud?.descripcion ?? "").trim(),
      }))
      .filter((solicitud) => solicitud.descripcion.length > 0),
  };
}
